/**
 * In lib rather than the app because the same links are built in two places
 * that can't import each other's copy: the notification feed in the browser,
 * and the mention emails the backend sends. `routes.tsx` consumes these
 * segments too — a route table and a path builder that disagree produce links
 * that 404 without either side changing.
 */

export const VERSION_SEGMENT = "versions";
export const CHANNEL_SIZE_SEGMENT = "sizes";
export const SIZE_VARIANT_SEGMENT = "size-variants";
/** Names a resize by its own `ad_brief_assets` id rather than by channel size. */
export const RESIZE_SEGMENT = "resizes";

/** The AMP board's root, and its project and task detail routes under it. */
export const PROJECTS_AREA_SEGMENT = "projects";
export const PROJECTS_LIST_SEGMENT = "all";
export const PROJECTS_TASKS_SEGMENT = "tasks";

/** Selects a thread once the ad, project or task is open. */
export const COMMENT_THREAD_PARAM = "comment";

/**
 * Turns the rail's "show resolved" filter on, for a link to a thread that is
 * already settled.
 *
 * In the link rather than in router state because the same URL is pasted,
 * bookmarked and reloaded, and state survives none of those — a link that only
 * worked on the first click would be the bug this param exists to fix.
 */
export const COMMENT_SHOW_RESOLVED_PARAM = "showResolved";

/** The list surface the ad opens over; both host the same concept routes. */
export type AdConceptParent =
  | { kind: "folder"; folderId: string }
  | { kind: "project"; projectId: string };

/**
 * A filed ad opens in its folder, where the team reviewing it works; the chat
 * that generated it is the fallback. Null when the ad has neither.
 */
export function adConceptParent({
  folderId,
  projectId,
}: {
  folderId: string | null;
  projectId: string | null;
}): AdConceptParent | null {
  if (folderId !== null) {
    return { kind: "folder", folderId };
  }

  return projectId === null ? null : { kind: "project", projectId };
}

export type AdConceptPath = {
  parent: AdConceptParent;
  adBriefId: string;
  assetId: string;
  /** Null for the primary render, which has no channel of its own. */
  channelSizeId: string | null;
};

/**
 * The channel size scopes the thread list, so a link without it would open the
 * ad and find nothing to select.
 *
 * Root-relative because the app's `navigate` injects the workspace slug itself;
 * for a link leaving the process, pass it through {@link absoluteAdStudioUrl}.
 */
export function buildCommentThreadPath({
  parent,
  adBriefId,
  assetId,
  channelSizeId,
  threadId,
  isThreadResolved = false,
}: AdConceptPath & {
  threadId: string;
  /**
   * Whether the thread is settled as of the link being built. Resolved threads
   * are filtered out of the rail, so the link has to ask for them back.
   */
  isThreadResolved?: boolean;
}): string {
  const parentPath =
    parent.kind === "folder"
      ? `/ad-studio/folders/${parent.folderId}`
      : `/ad-studio/projects/${parent.projectId}`;
  const detailPath = `${parentPath}/concepts/${adBriefId}/${assetId}`;
  const withSize = channelSizeId
    ? `${detailPath}/${CHANNEL_SIZE_SEGMENT}/${channelSizeId}`
    : detailPath;

  return `${withSize}?${commentThreadParams({ threadId, isThreadResolved })}`;
}

type CommentThreadLink = {
  threadId: string;
  /** As in {@link buildCommentThreadPath}. */
  isThreadResolved?: boolean;
};

/** A thread on an AMP board project. Root-relative, as {@link buildCommentThreadPath} is. */
export function buildAmpProjectCommentThreadPath({
  projectId,
  threadId,
  isThreadResolved = false,
}: CommentThreadLink & { projectId: string }): string {
  return `/${PROJECTS_AREA_SEGMENT}/${PROJECTS_LIST_SEGMENT}/${projectId}?${commentThreadParams({ threadId, isThreadResolved })}`;
}

/** A thread on an AMP board task. Root-relative, as {@link buildCommentThreadPath} is. */
export function buildAmpTaskCommentThreadPath({
  taskId,
  threadId,
  isThreadResolved = false,
}: CommentThreadLink & { taskId: string }): string {
  return `/${PROJECTS_AREA_SEGMENT}/${PROJECTS_TASKS_SEGMENT}/${taskId}?${commentThreadParams({ threadId, isThreadResolved })}`;
}

function commentThreadParams({
  threadId,
  isThreadResolved,
}: Required<CommentThreadLink>): string {
  const params = new URLSearchParams({ [COMMENT_THREAD_PARAM]: threadId });

  if (isThreadResolved) {
    params.set(COMMENT_SHOW_RESOLVED_PARAM, "true");
  }

  return params.toString();
}

/**
 * Self-serve Ad Studio workspaces are served from their own host and their paths
 * carry no slug, so they pass `workspaceSlug: null`.
 */
export function absoluteAdStudioUrl({
  baseUrl,
  workspaceSlug,
  path,
}: {
  /** App origin, no trailing slash. */
  baseUrl: string;
  /** Omitted for self-serve Ad Studio, whose host is the workspace. */
  workspaceSlug: string | null;
  /** Root-relative, leading slash. */
  path: string;
}): string {
  return workspaceSlug === null
    ? `${baseUrl}${path}`
    : `${baseUrl}/${workspaceSlug}${path}`;
}
